import React from "react";
import { Link } from "react-router-dom";
import { Phone, EnvelopeSimple, MapPin, Clock } from "@phosphor-icons/react";
import { COMPANY, SOCIALS, ATIKXZIO_SITE_URL } from "../lib/constants";
import { useLang } from "../lib/i18n";
import { InstallApp } from "./InstallApp";

const STR = {
  en: {
    ctaTitle: "Ready to see Atikxzio HMS in your hospital?",
    demo: "Book a free demo", talk: "Talk to our team",
    products: "Products", company: "Company", legal: "Legal", touch: "Get in touch",
    atikxzioLogin: "Atikxzio login",
    hmsLink: "Atikxzio HMS — Hospital management",
    productLinks: [
      ["Atikxzio CMH — Career & wellbeing", "/products/atikxzio-cmh"],
      ["Pricing", "/pricing"],
    ],
    companyLinks: [["About", "/about"], ["Careers", "/careers"], ["Contact", "/contact"]],
    legalLinks: [
      ["Terms & Conditions", "/terms"],
      ["Privacy Policy", "/privacy"],
      ["Refund & Cancellation", "/refund-policy"],
      ["Shipping & Delivery", "/shipping-policy"],
      ["Pricing", "/pricing"],
    ],
    secured: "Payments secured by RBI-authorised payment partners",
    hours: COMPANY.supportHours,
    rights: "All rights reserved.",
  },
  hi: {
    ctaTitle: "अपने अस्पताल में Atikxzio HMS देखने के लिए तैयार हैं?",
    demo: "फ्री डेमो बुक करें", talk: "हमारी टीम से बात करें",
    products: "प्रोडक्ट्स", company: "कंपनी", legal: "क़ानूनी", touch: "संपर्क करें",
    atikxzioLogin: "Atikxzio लॉगिन",
    hmsLink: "Atikxzio HMS — हॉस्पिटल मैनेजमेंट",
    productLinks: [
      ["Atikxzio CMH — करियर व मानसिक स्वास्थ्य", "/products/atikxzio-cmh"],
      ["प्राइसिंग", "/pricing"],
    ],
    companyLinks: [["हमारे बारे में", "/about"], ["करियर", "/careers"], ["संपर्क", "/contact"]],
    legalLinks: [
      ["नियम व शर्तें", "/terms"],
      ["प्राइवेसी नीति", "/privacy"],
      ["रिफ़ंड व कैंसलेशन", "/refund-policy"],
      ["शिपिंग व डिलीवरी", "/shipping-policy"],
      ["प्राइसिंग", "/pricing"],
    ],
    secured: "भुगतान RBI-अधिकृत पेमेंट पार्टनर्स द्वारा सुरक्षित",
    hours: "सोमवार–शनिवार, सुबह 9:30 – शाम 6:30 (IST)",
    rights: "सर्वाधिकार सुरक्षित।",
  },
};

const PAY_MARKS = ["UPI", "RuPay", "VISA", "Mastercard"];

const Column = ({ title, children }) => (
  <div>
    <p className="mb-4 font-display text-xs font-bold uppercase tracking-[0.18em] text-white">{title}</p>
    {children}
  </div>
);

export const Footer = () => {
  const { lang } = useLang();
  const s = STR[lang];
  const linkCls = "text-sm text-slate-400 transition-colors duration-200 hover:text-white";

  return (
    <footer className="bg-navy-deep text-slate-300" data-testid="footer">
      <div className="mx-auto max-w-[1200px] px-5 pt-20 lg:px-8">
        {/* CTA */}
        <div className="flex flex-col items-start justify-between gap-6 border-b border-white/10 pb-14 lg:flex-row lg:items-center">
          <h2 className="max-w-xl font-display text-3xl font-black tracking-tight text-white sm:text-4xl">{s.ctaTitle}</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" className="rounded-full bg-electric-ink px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#0F4FA3]" data-testid="footer-book-demo">{s.demo}</Link>
            <Link to="/contact" className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/10" data-testid="footer-talk-team">{s.talk}</Link>
          </div>
        </div>

        {/* Columns */}
        <div className="grid gap-10 py-14 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2 lg:col-span-4">
            <Link to="/" className="inline-flex items-center gap-3" data-testid="footer-logo">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/25">
                <img src="/brand/logo-mark-white.png" alt="Priyajit Systems" width="28" height="33" className="h-7 w-auto" />
              </span>
              <span className="leading-none">
                <span className="block font-display text-[15px] font-black tracking-tight text-white">PRIYAJIT <span className="text-electric-light">SYSTEMS</span></span>
                <span className="mt-1 block text-[8px] font-bold uppercase tracking-[0.3em] text-white/50">Private Limited</span>
              </span>
            </Link>
          </div>
          <Column title={s.products}>
            <ul className="space-y-3">
              <li><a href={ATIKXZIO_SITE_URL} target="_top" rel="noopener" className={linkCls} data-testid="footer-atikxzio-hms">{s.hmsLink}</a></li>
              {s.productLinks.map(([t, u]) => (<li key={u + t}><Link to={u} className={linkCls}>{t}</Link></li>))}
              <li>
                <a href={COMPANY.loginUrl} target="_top" rel="noopener" className={linkCls} data-testid="footer-atikxzio-login">{s.atikxzioLogin}</a>
              </li>
            </ul>
          </Column>
          <Column title={s.company}>
            <ul className="space-y-3">
              {s.companyLinks.map(([t, u]) => (<li key={u}><Link to={u} className={linkCls}>{t}</Link></li>))}
            </ul>
          </Column>
          <Column title={s.legal}>
            <ul className="space-y-3">
              {s.legalLinks.map(([t, u]) => (<li key={u + t}><Link to={u} className={linkCls} data-testid={`footer-legal-${u.slice(1)}`}>{t}</Link></li>))}
            </ul>
          </Column>
          <Column title={s.touch}>
            <ul className="space-y-3 text-sm text-slate-400">
              <li className="flex items-start gap-2.5"><Phone size={17} className="mt-0.5 shrink-0 text-electric" /><a href={`tel:${COMPANY.phoneTel}`} className="hover:text-white">{COMPANY.phone}</a></li>
              <li className="flex items-start gap-2.5"><EnvelopeSimple size={17} className="mt-0.5 shrink-0 text-electric" /><a href={`mailto:${COMPANY.email}`} className="hover:text-white">{COMPANY.email}</a></li>
              <li className="flex items-start gap-2.5"><MapPin size={17} className="mt-0.5 shrink-0 text-electric" /><span className="leading-relaxed">{COMPANY.address}</span></li>
              <li className="flex items-start gap-2.5"><Clock size={17} className="mt-0.5 shrink-0 text-electric" /><span>{s.hours}</span></li>
            </ul>
            {SOCIALS.length > 0 && (
              <div className="mt-5 flex gap-3">
                {SOCIALS.map((so) => (
                  <a key={so.url} href={so.url} target="_blank" rel="noreferrer" aria-label={so.label} className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors duration-200 hover:border-electric hover:text-electric">
                    {so.label[0]}
                  </a>
                ))}
              </div>
            )}
          </Column>
        </div>

        {/* Payment partners */}
        <div className="flex flex-col items-start gap-4 border-t border-white/10 py-7 sm:flex-row sm:items-center sm:justify-between" data-testid="footer-payments">
          <p className="text-xs font-semibold text-slate-400">{s.secured}</p>
          <div className="flex flex-wrap items-center gap-2.5">
            {PAY_MARKS.map((m) => (
              <span key={m} className="rounded-md border border-white/15 bg-white/[0.06] px-3 py-1.5 font-display text-[11px] font-bold tracking-wide text-slate-200" data-testid={`pay-mark-${m.toLowerCase()}`}>{m}</span>
            ))}
          </div>
        </div>

        {/* Bottom row */}
        <div className="flex flex-col gap-3 border-t border-white/10 py-7 text-xs text-slate-400 lg:flex-row lg:items-center lg:justify-between">
          <p data-testid="footer-legal-identity">
            © {new Date().getFullYear()} {COMPANY.legal} · CIN {COMPANY.cin} · GSTIN {COMPANY.gstin} · {s.rights}
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {s.legalLinks.slice(0, 4).map(([t, u]) => (
              <Link key={u} to={u} className="transition-colors duration-200 hover:text-white">{t}</Link>
            ))}
            <InstallApp variant="footer" />
          </div>
        </div>
      </div>
    </footer>
  );
};
